import {addAndRunAnalysisStepsV2} from 'analysis-tool/AnalysisActions';
import {
  SINGLE_DATASET_INPUT_STEP_DATA_NAME,
  SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {AnalysisGraphQL, AnalysisViewGraphQL} from 'analysis-tool/AnalysisTypes';
import {OverflowDataViewAction} from 'analysis-tool/data-view/DataViewContext';
import {checkCanModifyStep} from 'analysis-tool/EditStepHelpers';
import isAnalysisReadOnly from 'analysis-tool/isAnalysisReadOnly';
import {AnalysisMainViewItem, AnalysisMainViewItemType} from 'analysis-tool/ui/main-view/MainViewTypes';
import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import isParameterOptimizationEnabled from 'machine-learning/isParameterOptimizationEnabled';
import StepConfigEditorModal from 'pipelines/generalized-step-config-editor/StepConfigEditorModal';
import {StepReference} from 'pipelines/PipelineDAO';
import {PipelineStepConfig} from 'pipelines/PipelineTypes';
import {pipelineStepRawDataIsOfKind} from 'pipelines/StepDataUtils';
import runModal from 'ui-library/runModal';
import {withLoadingScreen} from 'util/Promises';

export default function getParameterOptimizationConfigurationAction(
  analysis: AnalysisGraphQL,
  view: AnalysisViewGraphQL,
  onChangeSelectedItem: (newSelectedItem: AnalysisMainViewItem) => void
): OverflowDataViewAction | null {
  // Keep logic in sync with `client/analysis-tool/ui/main-view/DataFrameView.tsx`
  const {step, stepOutputReference} = view.lineage[view.lineage.length - 1];
  if (
    isAnalysisReadOnly(analysis) ||
    !isParameterOptimizationEnabled() ||
    stepOutputReference == null ||
    stepOutputReference.rawDataIndex == null
  ) {
    return null;
  }

  const rawData = stepOutputReference.outputStepData.rawDatas[stepOutputReference.rawDataIndex];

  if (!pipelineStepRawDataIsOfKind(PipelineStepDataKind.DATASET, rawData)) {
    return null;
  }

  const stepType = step.stepType;

  if (stepType === PipelineStepType.PARAMETER_OPTIMIZATION_CONFIGURATION) {
    return {
      icon: 'settings',
      label: 'View Parameter Optimization Configuration',
      onClick: () => {
        // TODO(BNCH-123755): Make all step configs viewable in right sidebar instead of modals
        runModal(StepConfigEditorModal, {
          stepType,
          stepVersion: step.stepVersion,
          inputDataFrameSpec: {
            name: rawData.name,
            columns: rawData.columns,
          },
          initialConfig: step.config,
          readOnly: true,
        });
      },
    };
  }

  const {canEdit, message} = checkCanModifyStep(analysis, step.id, 'table', true);

  return {
    icon: 'settings',
    onClick: () =>
      runModal(StepConfigEditorModal, {
        stepType: PipelineStepType.PARAMETER_OPTIMIZATION_CONFIGURATION,
        stepVersion: 0,
        inputDataFrameSpec: {
          name: rawData.name,
          columns: rawData.columns,
        },
        onRequestSubmit: (
          config: PipelineStepConfig<PipelineStepType.PARAMETER_OPTIMIZATION_CONFIGURATION, 0>
        ) => {
          handleAddAndRunParameterOptimizationStep(
            analysis.id,
            step.id,
            stepOutputReference.outputStepData.name,
            rawData.id,
            config,
            onChangeSelectedItem
          );
        },
      }),
    label: 'Configure Parameter Optimization',
    isDisabled: !canEdit,
    helpText: !canEdit ? message : undefined,
  };
}

export async function handleAddAndRunParameterOptimizationStep(
  analysisId: string,
  inputStepId: string,
  inputStepDataName: string,
  inputDataFrameId: string,
  config: PipelineStepConfig<PipelineStepType.PARAMETER_OPTIMIZATION_CONFIGURATION, number>,
  onChangeSelectedItem: (newSelectedItem: AnalysisMainViewItem) => void
): Promise<void> {
  const newSteps = await withLoadingScreen(
    addAndRunAnalysisStepsV2(analysisId, [
      {
        step: {
          stepType: PipelineStepType.PARAMETER_OPTIMIZATION_CONFIGURATION,
          stepVersion: 0,
          stepConfig: config,
          stepInputParents: {
            [SINGLE_DATASET_INPUT_STEP_DATA_NAME]: [
              {
                stepReference: {
                  type: StepReference.STEP_ID,
                  stepId: inputStepId,
                },
                stepDataName: inputStepDataName,
              },
            ],
          },
          stepInput: {
            inputDataFrameId,
          },
        },
      },
      {
        step: {
          stepType: PipelineStepType.EXPLORATORY_DATA_ANALYSIS,
          stepVersion: 0,
          stepConfig: {
            labelColumn: config.labelColumn,
            featureColumns: config.featureColumns,
            excludedColumns: config.excludedColumns,
          },
          stepInputParents: {
            [SINGLE_DATASET_INPUT_STEP_DATA_NAME]: [
              {
                stepReference: {
                  type: StepReference.INDEX,
                  index: 0,
                },
                stepDataName: SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
              },
            ],
          },
        },
      },
    ]),
    'Initializing Parameter Optimization...'
  );

  const newStepId = newSteps[0].id;

  // TODO(BNCH-123757): Adapt parameter optimization to new views and sidebar
  onChangeSelectedItem({
    type: AnalysisMainViewItemType.DATA_FRAME,
    stepId: newStepId,
    dataFrameId: inputDataFrameId,
  });
}
